import type { ComponentProps } from "react";
import { cn } from "@/client/lib/utils";

// Compact "tactical" table primitives: dense rows, small uppercase headers, hairline borders.
// Shared by the generic DataTable and by hand-composed sectioned tables (e.g. Aanbod). Dumb
// styled wrappers; callers add alignment/monospace via className on individual cells.

export function Table({ className, ...props }: ComponentProps<"table">) {
  return (
    <div className="w-full overflow-x-auto rounded-lg border bg-card">
      <table className={cn("w-full border-collapse text-sm", className)} {...props} />
    </div>
  );
}

export function TableHeader({ className, ...props }: ComponentProps<"thead">) {
  return <thead className={cn("border-b bg-muted/50", className)} {...props} />;
}

export function TableBody({ className, ...props }: ComponentProps<"tbody">) {
  return <tbody className={className} {...props} />;
}

export function TableRow({ className, ...props }: ComponentProps<"tr">) {
  return (
    <tr
      className={cn("border-b transition-colors last:border-b-0 hover:bg-muted/40", className)}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: ComponentProps<"th">) {
  return (
    <th
      className={cn(
        "h-8 whitespace-nowrap px-3 text-left align-middle text-xs font-medium text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: ComponentProps<"td">) {
  return <td className={cn("px-3 py-1.5 align-middle", className)} {...props} />;
}

// A full-width grouping/section row spanning all columns (used by sectioned tables like Aanbod).
export function TableSectionRow({
  colSpan,
  className,
  ...props
}: ComponentProps<"td"> & { colSpan: number }) {
  return (
    <tr className="border-b bg-muted/30 last:border-b-0">
      <td
        colSpan={colSpan}
        className={cn(
          "px-3 py-1.5 text-xs font-semibold text-muted-foreground",
          className,
        )}
        {...props}
      />
    </tr>
  );
}
